import React, { FunctionComponent, PropsWithChildren, useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { Spin } from 'antd';
import { AppStore, userActions } from '@akademiasaas/shared';
import { useQuery } from '~/hooks/useQuery';
import { useAppDispatch, auth } from '~/initializeStore';
import { getSafeAuthRedirectPath } from '~/pages/Auth/helpers/getAuthRedirectPath';

interface OwnProps {}

type Props = PropsWithChildren<OwnProps>;

const AuthChecker: FunctionComponent<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isUserResolved, setIsUserResolved] = useState(false);
  const user = useSelector((store: AppStore) => store.user);
  const userUid = user.data?.uid;
  const query = useQuery();

  const handleUserData = async (newUser: User | null) => {
    // A lingering anonymous session (from the public submission page) must not
    // block the login page — drop it instead of redirecting away from /auth.
    if (newUser?.isAnonymous && history.location.pathname.startsWith('/auth')) {
      dispatch(userActions.logOutUser());
      if (!isUserResolved) {
        setTimeout(() => {
          setIsUserResolved(true);
        }, 0);
      }

      return;
    }

    if (newUser && newUser.uid !== userUid) {
      dispatch(userActions.setLoggedUserData(newUser));
    }

    // Handle logout - redirect to auth page when user becomes null
    if (!newUser && userUid && !history.location.pathname.startsWith('/auth') && !history.location.pathname.startsWith('/egzamin/')) {
      history.push('/auth/login');
    }

    // The verify screen lives under /auth but is meant for logged-in users, so
    // it must be exempt - otherwise an unconfirmed account ping-pongs between
    // here and the ProtectedRoute redirect.
    const isVerifyEmailPath = history.location.pathname.startsWith('/auth/verify-email');

    if (history.location.pathname.startsWith('/auth') && newUser && !isVerifyEmailPath) {
      const redirectPath =
        getSafeAuthRedirectPath(query.get('redirectTo')) ||
        getSafeAuthRedirectPath(query.get('continue'));
      history.push(redirectPath || '/');
    }
    if (!isUserResolved) {
      setTimeout(() => {
        setIsUserResolved(true);
      }, 0);
    }
  };

  const debouncedCallback = useDebouncedCallback(handleUserData, 400);

  useEffect(() => {
    (async () => {
      const unsubscribe = onAuthStateChanged(auth, debouncedCallback);

      return () => unsubscribe();
    })();
  }, [debouncedCallback, isUserResolved, userUid]);

  if (!isUserResolved) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthChecker;
